import { formatFinanceDate } from "@/utils/format-finance-date";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoveLeft, MoveRight } from "lucide-react";

import { Button, Skeleton } from "@/components/ui";
import {
  useFetchFinanceEntryByIdQuery,
  usePermission,
  useToast,
  useWriteOffFinanceEntryMutation,
} from "@/hooks";
import { FinanceEntry, Installment } from "@/types";
import { DisplayError, formatDate, formatValue } from "@/utils";
import { defaultEntriesCategories, defaultFinancialAccounts } from "@/helpers";

type Props = {
  id: string;
  handleClose: () => void;
};

export function FormExchangeFinanceEntry({ id, handleClose }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { data: financeEntry, isLoading: isLoadingEntry } =
    useFetchFinanceEntryByIdQuery({ id });
  const { mutateAsync, isLoading: isLoadingWriteOff } =
    useWriteOffFinanceEntryMutation();
  const [entry, setEntry] = useState<FinanceEntry | null>(null);
  const [page, setPage] = useState(1);

  function goToPreviousPage() {
    setPage(page - 1);
  }

  function goToNextPage() {
    setPage(page + 1);
  }

  async function handleClick(id: string) {
    await mutateAsync(
      {
        id,
      },
      {
        onSuccess: ({ message }) => {
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message.toLowerCase()}`),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      }
    );
    setTimeout(() => {
      handleClose();
    }, 2000);
  }

  useEffect(() => {
    if (financeEntry) {
      setEntry(financeEntry);
    }
  }, [financeEntry, id]);

  return (
    <div className="max-2xl:h-[94vh] max-sm:h-[90vh] max-2xl:overflow-y-auto">
      {!entry ? (
        <FormExchangeFinanceEntryData
          id={id}
          entry={entry}
          isLoadingEntry={isLoadingEntry}
          isLoadingWriteOff={isLoadingWriteOff}
          handleClick={handleClick}
        />
      ) : (
        <>
          {entry?.installment?.entries &&
            entry?.installment?.entries.length > 0 ? (
            entry.installment.entries
              .slice((page - 1) * 1, page * 1)
              .map((item, index) => (
                <FormExchangeFinanceEntryData
                  key={index}
                  id={item.id}
                  entry={{
                    entry: entry.installment.entry?.toString() || "",
                    category: entry.category,
                    patient: entry.patient,
                    date: item.date,
                    mode: item.mode,
                    pay: item.pay.toString(),
                    name: entry.name,
                    payday: item.payday?.toString() || "",
                    type: entry.type,
                    user: entry.user,
                    value: item.value.toString(),
                    installment: entry.installment,
                    account: entry.account,
                    payment_method: entry.payment_method,
                  }}
                  installment={entry.installment}
                  isLoadingEntry={isLoadingEntry}
                  isLoadingWriteOff={isLoadingWriteOff}
                  handleClick={handleClick}
                />
              ))
          ) : (
            <FormExchangeFinanceEntryData
              id={id}
              entry={entry}
              isLoadingEntry={isLoadingEntry}
              isLoadingWriteOff={isLoadingWriteOff}
              handleClick={handleClick}
            />
          )}
        </>
      )}
      {entry?.installment?.entries &&
        entry?.installment?.entries.length > 0 && (
          <div className="flex flex-row justify-center gap-2 mt-10">
            <Button
              variant="secondary"
              className="gap-2 flex flex-row items-center"
              onClick={goToPreviousPage}
              disabled={page === 1}
            >
              <MoveLeft />
              <span>{t("LABEL.previous")}</span>
            </Button>
            <div className="rounded-lg px-4 py-1 flex items-center justify-center bg-teal-700 text-white text-sm">
              <span>
                {t("LABEL.installments")} {t("LABEL.of")} {page} {t("LABEL.of")}{" "}
                {entry?.installment?.total_installments}
              </span>
            </div>
            <Button
              variant="secondary"
              className="gap-2 flex flex-row items-center"
              onClick={goToNextPage}
              disabled={page === entry?.installment?.total_installments}
            >
              <span>{t("LABEL.next")}</span>
              <MoveRight />
            </Button>
          </div>
        )}
    </div>
  );
}

interface FormExchangeFinanceEntryItemProps {
  title: string;
  value: any;
}

function FormExchangeFinanceEntryItem({
  title,
  value,
}: FormExchangeFinanceEntryItemProps) {
  return (
    <p className="text-sm text-zinc-500">
      <strong>{title}:</strong> {value}
    </p>
  );
}

type Entry = Omit<
  FinanceEntry,
  | "id"
  | "fine_for_delay"
  | "interest_rate"
  | "payment_method"
  | "attachment_path"
  | "attachment_description"
  | "created_at"
  | "status"
  | "is_entry"
  | "employee"
  | "procedure"
  | "period"
  | "total"
> & {
  payment_method: string;
};

interface FormExchangeFinanceEntryDataProps {
  id: string;
  entry?: Entry | null;
  installment?: Installment | null;
  isLoadingEntry: boolean;
  isLoadingWriteOff: boolean;
  handleClick: (id: string) => void;
}

function FormExchangeFinanceEntryData({
  entry,
  installment,
  isLoadingEntry,
  isLoadingWriteOff,
  handleClick,
  id,
}: FormExchangeFinanceEntryDataProps) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();

  function getType(type: string) {
    switch (type) {
      case "income":
        return t("LABEL.income");
      case "expense":
        return t("LABEL.expense");
      default:
        return t("LABEL.unknown");
    }
  }
  return (
    <div className="flex justify-between items-start p-4">
      <div className="space-y-3 w-full flex-1 border-r border-zinc-200 h-full">
        {entry && !isLoadingEntry ? (
          <h1 className="text-lg font-semibold text-teal-700">{entry.name}</h1>
        ) : (
          <Skeleton className="w-2/4 h-10" />
        )}
        <div className="space-y-2">
          {entry && !isLoadingEntry ? (
            <>
              <FormExchangeFinanceEntryItem
                title="Conta"
                value={
                  defaultFinancialAccounts.includes(entry.account.name)
                    ? t(`LABEL.${entry.account.name.toLocaleLowerCase()}`)
                    : entry.account.name
                }
              />
              <FormExchangeFinanceEntryItem
                title="Lançador"
                value={entry.user.name || ""}
              />
              <FormExchangeFinanceEntryItem
                title="Tipo"
                value={getType(entry.type)}
              />
              <FormExchangeFinanceEntryItem
                title="Recorrência"
                value={t(`LABEL.${entry.mode}`)}
              />
              <FormExchangeFinanceEntryItem
                title="Categoria"
                value={
                  defaultEntriesCategories.includes(entry.name.toLowerCase())
                    ? <span className="capitalize">{t(`LABEL.${entry.name.toLowerCase()}`)}</span>
                    : <span className="capitalize">{entry.name}</span>
                }
              />
              <FormExchangeFinanceEntryItem
                title="Pagamento"
                value={t(`LABEL.${entry.payment_method.toLocaleLowerCase()}`)}
              />
            </>
          ) : (
            Array.from({ length: 6 }).map((_, index) => (
              <Skeleton className="w-2/4 h-6" key={index} />
            ))
          )}
        </div>
      </div>
      <div className="flex-1 px-5">
        <div className="space-y-2">
          {entry && !isLoadingEntry ? (
            <>
              <div className="p-4 space-y-2 border border-zinc-100 bg-zinc-100/80 rounded-lg w-full">
                <h3 className="text-sm font-semibold text-teal-700">
                  {formatValue(Number(entry.value))}
                </h3>
                <div className="space-y-1">
                  <FormExchangeFinanceEntryItem
                    title="Vencimento"
                    value={formatFinanceDate(entry.date)}
                  />
                  <FormExchangeFinanceEntryItem
                    title="Baixa"
                    value={
                      entry.payday ? formatDate(entry.payday, false, true) : ""
                    }
                  />
                  {installment && (
                    <>
                      <FormExchangeFinanceEntryItem
                        title="Entrada"
                        value={formatValue(Number(installment.entry))}
                      />
                      <FormExchangeFinanceEntryItem
                        title="Valor total"
                        value={formatValue(Number(installment.amount))}
                      />
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={() => handleClick(id)}
                isLoading={isLoadingWriteOff}
                disabled={!checkPermission("finance entry", "pay finance entry") || isLoadingWriteOff || !!entry.payday}
              >
                Dar baixa
              </Button>
            </>
          ) : (
            <>
              <Skeleton className="w-full h-40" />
              <Skeleton className="w-28 h-10" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
